import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * Nymbl custom theme preset extending PrimeNG Aura theme.
 *
 * This preset customizes the Aura theme with Nymbl-specific:
 * - Color palette
 * - Typography
 * - Component styling overrides
 *
 * Usage in Angular app:
 * ```typescript
 * import { NymblTheme } from '@nymbl/theme-primeng';
 * import { providePrimeNG } from 'primeng/config';
 *
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     providePrimeNG({
 *       theme: {
 *         preset: NymblTheme,
 *         options: {
 *           darkModeSelector: '.dark-mode',
 *         },
 *       },
 *     }),
 *   ],
 * };
 * ```
 */
export const NymblTheme = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '4px',
      md: '6px',
      lg: '8px',
      xl: '12px'
    },
    // Status color palettes - maps to status badge colors
    blue: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
      950: '#172554'
    },
    red: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
      950: '#450a0a'
    },
    orange: {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316',
      600: '#ea580c',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
      950: '#431407'
    },
    green: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
      950: '#052e16'
    },
    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
      950: '#030712'
    },
    // Nymbl 3 brand primary - deep purple (#26045E)
    purple: {
      50: '#f3e7f5',
      100: '#e0c9e8',
      200: '#c9a8d8',
      300: '#ad7ebf',
      400: '#9370db', // dark mode primary color
      500: '#26045E', // light mode primary color ($primaryColor)
      600: '#1f0349',
      700: '#180239',
      800: '#110127',
      900: '#0a0015',
      950: '#05000a'
    },
    // Nymbl 3 brand secondary - pink (#ff5959)
    pink: {
      50: '#fff0f0',
      100: '#ffe0e0',
      200: '#ffc0c0',
      300: '#ff9999',
      400: '#ff8787', // dark mode secondary color
      500: '#ff5959', // light mode secondary color ($secondaryColor)
      600: '#e53e3e',
      700: '#c53030',
      800: '#9b2c2c',
      900: '#742020',
      950: '#4a1414'
    }
  },
  semantic: {
    // Primary color palette - references Nymbl 3 brand purple primitive
    primary: {
      50: '{purple.50}',
      100: '{purple.100}',
      200: '{purple.200}',
      300: '{purple.300}',
      400: '{purple.400}',
      500: '{purple.500}',
      600: '{purple.600}',
      700: '{purple.700}',
      800: '{purple.800}',
      900: '{purple.900}',
      950: '{purple.950}'
    },
    // Status semantic colors - maps to status badge colors
    success: {
      50: '{green.50}',
      100: '{green.100}',
      200: '{green.200}',
      300: '{green.300}',
      400: '{green.400}',
      500: '{green.500}',
      600: '{green.600}',
      700: '{green.700}',
      800: '{green.800}',
      900: '{green.900}',
      950: '{green.950}'
    },
    info: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}'
    },
    warn: {
      50: '{orange.50}',
      100: '{orange.100}',
      200: '{orange.200}',
      300: '{orange.300}',
      400: '{orange.400}',
      500: '{orange.500}',
      600: '{orange.600}',
      700: '{orange.700}',
      800: '{orange.800}',
      900: '{orange.900}',
      950: '{orange.950}'
    },
    danger: {
      50: '{red.50}',
      100: '{red.100}',
      200: '{red.200}',
      300: '{red.300}',
      400: '{red.400}',
      500: '{red.500}',
      600: '{red.600}',
      700: '{red.700}',
      800: '{red.800}',
      900: '{red.900}',
      950: '{red.950}'
    },
    // Focus ring configuration - maps SASS focus mixins
    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
      shadow: '0 0 0 0.2rem {primary.200}'
    },
    colorScheme: {
      light: {
        // Surface colors for light mode - maps to Light Neutral (#ffffff bg, #111827 text)
        surface: {
          0: '#ffffff',
          50: '{gray.50}',
          100: '{gray.100}',
          200: '{gray.200}',
          300: '{gray.300}',
          400: '{gray.400}',
          500: '{gray.500}',
          600: '{gray.600}',
          700: '{gray.700}',
          800: '{gray.800}',
          900: '{gray.900}',
          950: '{gray.950}'
        },
        // Primary color states for light mode
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}'
        },
        // Secondary color states for light mode - light purple (#9370db)
        // Emits --p-secondary-color and --p-secondary-background, used for active route highlighting
        secondary: {
          color: '{purple.400}',
          contrastColor: '#ffffff',
          hoverColor: '{purple.500}',
          activeColor: '{purple.600}'
        },
        // Highlight colors for selections
        highlight: {
          background: '{primary.50}',
          focusBackground: '{primary.100}',
          color: '{primary.700}',
          focusColor: '{primary.800}'
        },
        mask: {
          background: 'rgba(0, 0, 0, 0.2)',
          color: '{surface.200}'
        },
        // Status colors for light mode
        success: {
          background: 'lightgreen', // status-approved background
          color: 'darkgreen' // status-approved text
        },
        info: {
          background: 'lightblue', // status-received background
          color: 'darkblue' // status-received text
        },
        warn: {
          background: 'lightsalmon', // status-requested background
          color: 'orangered' // status-requested text
        },
        danger: {
          background: 'lightpink', // status-denied background
          color: 'darkred' // status-denied text
        }
      },
      dark: {
        // Surface colors for dark mode - maps to Dark Neutral (#1f2937 bg, #f9fafb text)
        surface: {
          0: '#ffffff',
          50: '{gray.950}',
          100: '{gray.900}',
          200: '{gray.800}',
          300: '{gray.700}',
          400: '{gray.600}',
          500: '{gray.500}',
          600: '{gray.400}',
          700: '{gray.300}',
          800: '{gray.200}',
          900: '{gray.100}',
          950: '{gray.50}'
        },
        // Primary color states for dark mode
        primary: {
          color: '{primary.400}',
          contrastColor: '{surface.900}',
          hoverColor: '{primary.300}',
          activeColor: '{primary.200}'
        },
        // Secondary color states for dark mode - light purple (#ad7ebf)
        // Emits --p-secondary-color and --p-secondary-background, used for active route highlighting
        secondary: {
          color: '{purple.300}',
          contrastColor: '{surface.900}',
          hoverColor: '{purple.400}',
          activeColor: '{purple.500}'
        },
        // Highlight colors for selections in dark mode
        highlight: {
          background: '{primary.800}',
          focusBackground: '{primary.700}',
          color: '{primary.100}',
          focusColor: '{primary.50}'
        },
        // Status colors for dark mode
        success: {
          background: '{green.800}',
          color: '{green.100}'
        },
        info: {
          background: '{blue.800}',
          color: '{blue.100}'
        },
        warn: {
          background: '{orange.800}',
          color: '{orange.100}'
        },
        danger: {
          background: '{red.800}',
          color: '{red.100}'
        }
      }
    }
  },
  components: {
    // Button component tokens
    // Structure: button.root.* for main button properties
    button: {
      root: {
        borderRadius: '{borderRadius.md}',
        paddingX: '1rem',
        paddingY: '0.5rem',
        gap: '0.5rem',
        sm: {
          fontSize: '0.875rem',
          paddingX: '0.625rem',
          paddingY: '0.375rem'
        },
        lg: {
          fontSize: '1.125rem',
          paddingX: '1.25rem',
          paddingY: '0.625rem'
        }
      }
    },

    // DataTable component tokens - maps SASS $subTable* variables
    // Structure: datatable.headerCell.*, datatable.bodyCell.*, etc.
    datatable: {
      headerCell: {
        padding: '0.75rem 1rem',
        background: '{surface.50}',
        borderColor: '{surface.200}',
        color: '{surface.700}'
      },
      bodyCell: {
        padding: '0.75rem 1rem'
      },
      footerCell: {
        padding: '0.75rem 1rem'
      },
      // Sort icon styling - fixes dark icons issue
      sortIcon: {
        color: '{surface.500}',
        hoverColor: '{surface.600}'
      },
      // Filter styling
      filter: {
        inlineGap: '0.5rem',
        overlaySelect: {
          background: '{surface.0}',
          borderColor: '{surface.200}',
          color: '{surface.700}'
        }
      }
    }
  }
});
